import { StrictMode } from 'react';

import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';

import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import '@fontsource/roboto/300.css';
import '@fontsource/roboto/400.css';
import '@fontsource/roboto/500.css';
import '@fontsource/roboto/700.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Force aggressive cache busting so each visit behaves like a hard reload.
const clearFrontendCaches = () => {
  if (typeof window === 'undefined') return;

  if ('caches' in window) {
    caches.keys().then((keys) => keys.forEach((key) => caches.delete(key)));
  }

  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => registration.unregister());
    });
  }
};

clearFrontendCaches();

const theme = createTheme({
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          overflowX: 'hidden',
          width: '100%',
          maxWidth: '100vw',
        },
        body: {
          overflowX: 'hidden',
          width: '100%',
          maxWidth: '100vw',
          margin: 0,
          padding: 0,
        },
        '#root': {
          overflowX: 'hidden',
          width: '100%',
          maxWidth: '100vw',
        },
        '*, *::before, *::after': {
          boxSizing: 'border-box',
        },
      },
    },
  },
  palette: {
    type: 'light',
    primary: {
      main: '#5e894f',
      cancel: '#d44242',
    },
    secondary: {
      main: '#f5f5f5',
    },
    tertiary: {
      main: '#d44242',
    },
    background: {
      paper: '#ffffff',
      default: '#d6d8d5',
      menus: '#ededed',
      formTables: '#f2f2f2',
    },
    divider: 'rgba(0,0,0,0.12)',
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      retry: false,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      onError: (error) => {
        console.error('Query error:', error);
        if (error?.response) {
          console.error('Response error:', {
            status: error.response.status,
            statusText: error.response.statusText,
            data: error.response.data,
            url: error.response.config?.url,
          });
          if (error.response.status === 401) {
            queryClient.clear();
            localStorage.removeItem('auth_token');
            window.location.href = '/';
          }
        } else if (error?.request) {
          console.error('Network error:', error.message);
        }
        if (error?.response?.status >= 500) {
          console.error('Server error detected');
        }
      },
    },
    mutations: {
      retry: false,
      onError: (error, variables, context) => {
        console.error('Mutation error:', error);
        if (error?.response) {
          console.error('Mutation failed:', {
            status: error.response.status,
            data: error.response.data,
            variables: variables,
          });
        }
        if (error?.response?.status === 401) {
          queryClient.clear();
          localStorage.removeItem('auth_token');
          window.location.href = '/';
        }
      },
    },
  },
});

const rootElement = document.getElementById('root');
const root = createRoot(rootElement);

root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <App />
        </ThemeProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>
);

window.clearQueryCache = () => {
  queryClient.clear();
};
window.getQueryCache = () => {};
window.invalidateQueries = (queryKey) => {
  queryClient.invalidateQueries({ queryKey });
};
